import styled from 'styled-components';
import { useCallback, useEffect, useState } from 'react';
import { CategoryData, RegionData, ThemeData } from '../quantData';
import { Link, useSearchParams } from 'react-router-dom';
import QuantService from '../../../service/quant/Quant';
import UserService from '../../../service/User';
import { useModal } from '../../../context/ModalContext';
import ThemeSelectModal from './ThemeSelectModal';
import { REGION_CODE_MAP } from '../../../utils/auth';
import { useQuantContext } from '../../../context/QuantContext';
import RegionSelect from './RegionSelect';
import CategorySelect from './CategorySelect';
import ResultTable from './ResultTable';
import { useUser } from '../../../context/UserContext';

const themeDescriptionMap: Record<string, React.ReactNode> = {
  저평가주: (
    <>
      감정가가 땅집고 AI 추정 시세보다 낮은 물건. 수익 커버리지를 늘리고 투자
      리스크를 최소화하는 전략
    </>
  ),
  공시지가보다낮주: (
    <>
      매입가가 공시지가보다 낮은 물건. 공시지가는 실제 시세의 5-70% 수준에서
      형성되기 때문에 시세 대비 최대 50% 이상 할인가능한 저평가 극대화 전략
    </>
  ),
  전세가보다낮주: (
    <>
      매입가보다 전세보증금이 높은 물건. 레버리지 효과를 극대화하여 실질
      투자금액을 최소화 가능한 전략
    </>
  ),
  전세우위주: (
    <>
      전세 보증금을 활용해 초기 투자금 부담을 낮추고, 가격 상승 시 시세차익을
      노릴 수 있는 갭 투자형 아파트 선별 전략(아파트 한정)
    </>
  ),
  월세우위주: (
    <>
      지역별 월세 시세와 임대 회전율을 분석하여 안정적인 현금흐름이 발생하는
      물건을 선별하는 전략(아파트 한정)
    </>
  ),
  평균보다낮주: (
    <>
      인근 유사 물건의 평균 시세 대비 가격이 낮게 형성된 부동산을 선별하여, 향후
      시세 정상화 또는 회복 시 이익을 기대할 수 있는 전략
    </>
  ),
  하자없주: (
    <>
      권리관계 하자가 없는 물건을 선별하여 법적 이슈 및 지체상황을 방지하는 전략
    </>
  ),
  시세차익주: (
    <>
      주변 평균 시세보다 저가에 매입하여 향후 시세 상승 여지가 충분한 물건을
      찾는 전략. AI분석을 통해 일시적으로 저평가된 물건을 식별하고, 가격 편차가
      큰 물견을 중심으로 분석해 투자 기회를 포착
    </>
  ),
  저평가부실채권주: (
    <>
      감정가가 땅집고 AI 추정 시세보다 낮은 <strong>부실채권</strong>. 수익
      커버리지를 늘리고 투자 리스크를 최소화하는 전략
    </>
  ),
  소액투자주: (
    <>
      입찰에 필요한 자기자본이 3천만원 이내인 물건. 초기 투자 부담이 적어
      소액으로 경공매 시작해보고 싶은 분께 적합한 전략
    </>
  ),
  시세상승주: (
    <>
      최근 1년동안 호재로 부동산이 상승하는 지역을 분석하여 선제적 투자하는
      전략. 호재 발생 초기에 길목을 선점하여 투자 수익을 극대화
    </>
  ),
  이자보다월세주: (
    <>
      대출이 있어도 월세 수익으로 이자를 충당하고도 남는 물건을 찾는 전략.
      레버리지를 활용한 자기자본 수익률 극대화로 현금흐름 확보 가능
    </>
  ),
};

const Quant2 = () => {
  const { user } = useUser();
  const { modalDispatch } = useModal();
  const {
    selectedRegions,
    selectedCategory,
    setSelectedRegions,
    setSelectedCategory,
  } = useQuantContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const indexes = searchParams.get('indexes')?.split(',').map(Number) || [];
  const selectedThemes = indexes.map((idx) => ThemeData[idx]).filter(Boolean);

  const [themeSelectModal, setThemeSelectedModal] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const removeTheme = (targetIndex: number) => {
    const nextIndexes = indexes.filter((i) => i !== targetIndex);
    const newParams = new URLSearchParams(searchParams);
    if (nextIndexes.length === 0) {
      newParams.delete('indexes');
    } else {
      newParams.set('indexes', nextIndexes.join(','));
    }
    setSearchParams(newParams);
  };

  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [p3, setP3] = useState('');
  const [p4, setP4] = useState('');

  const updateQuery = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(key, value);
    setSearchParams(newParams);
  };

  const getCode = (region: string) => {
    const regObj = {
      전국: '',
      서울: '11',
      경기: '41',
      인천: '28',
      부산: '26',
      대전: '30',
      대구: '27',
      울산: '31',
      세종: '36',
      강원: '51',
      충북: '43',
      충남: '44',
      경북: '47',
      경남: '48',
      광주: '29',
      전북: '52',
      전남: '46',
      제주: '50',
    };
    return regObj[region as '전국'];
  };

  const getList = async () => {
    try {
      if (selectedThemes.length === 0) {
        setP1('0');
        setP2('0');
        setP3('0');
        setP4('0');
        return;
      }
      const themeNames = selectedThemes.map((t) => t.name).join(',');
      const regions = selectedRegions.map((r) => getCode(r)).join(',');

      const result = await QuantService.stats({
        theme: themeNames,
        category: selectedCategory.join(','),
        ctpCode: regions,
      });
      if (result.data) {
        result.data.map((el: any, idx: number) => {
          if (idx === 0) {
            setP1(
              (Math.floor(Number(el.profit_rate || 0) * 10) / 10).toFixed(1) ||
                '-',
            );
            setP2(
              (Math.floor(Number(el.loss_rate || 0) * 10) / 10).toFixed(1) ||
                '-',
            );
            setP3('-');
            setP4('-');
          } else if (idx === 1) {
            setP3(
              (Math.floor(Number(el.profit_rate || 0) * 10) / 10).toFixed(1) ||
                '-',
            );
            setP4(
              (Math.floor(Number(el.loss_rate || 0) * 10) / 10).toFixed(1) ||
                '-',
            );
          }
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const func = async () => {
    getList();
    // if (await UserService.userGrade()) {
    // } else {
    //   modalDispatch({ type: 'MC1' });
    // }
  };

  useEffect(() => {
    func();
  }, [selectedRegions, selectedCategory, selectedThemes]);

  const [userGrade, setUserGrade] = useState<string | null>(null);
  const [userRegions, setUserRegions] = useState<string[]>([]);

  useEffect(() => {
    if (!user) return;
    setUserGrade(user.plan_grade);

    if (user.plan_grade === 'P' || user.plan_grade === 'T') {
      setUserRegions(RegionData);
    } else {
      const planRegion = user.plan_region ?? 'A';
      const regions = planRegion.split(',');

      const allowed = new Set<string>();
      allowed.add('전국');

      regions.forEach((r: string) => {
        if (r === 'A') {
          RegionData.forEach((region) => allowed.add(region));
        } else if (r === 'F') {
          RegionData.forEach((region) => {
            if (!['서울', '경기', '인천', '전국'].includes(region)) {
              allowed.add(region);
            }
          });
        } else {
          REGION_CODE_MAP[r]?.forEach((area) => allowed.add(area));
        }
      });

      setUserRegions(Array.from(allowed));
    }
  }, [user]);

  return (
    <Box gap={8} style={{ background: '#F0F0F0', paddingBottom: '8px' }}>
      <ThemeBox>
        <ThemeBoxTitleSection>
          <ThemeBoxTitle>테마 선택</ThemeBoxTitle>
          <ThemeSelectButton onClick={() => setThemeSelectedModal(true)}>
            <img src="/assets/images/plus/plus-black.svg" alt="plus" />
            테마주 선택하기
          </ThemeSelectButton>
        </ThemeBoxTitleSection>
        <SelectedThemeBox>
          {selectedThemes.map((theme, idx) => (
            <ThemeItem key={idx}>
              <ThemeItemHeader>
                {theme.name}
                <img
                  src="/assets/images/close/close-alternative.svg"
                  alt="close button"
                  onClick={() => removeTheme(indexes[idx])}
                />
              </ThemeItemHeader>
              {(showAll || idx < 3) && (
                <ThemeItemDescription>
                  {themeDescriptionMap[theme.name]}
                </ThemeItemDescription>
              )}
            </ThemeItem>
          ))}
        </SelectedThemeBox>
        {selectedThemes.length > 3 && (
          <MoreButtonBox>
            <MoreButton
              onClick={() => setShowAll((prev) => !prev)}
              showAll={showAll}
            >
              {showAll ? '간략히' : '자세히'} 보기
              <img src="/assets/images/arrow/arrow-up-gray.svg" alt="" />
            </MoreButton>
          </MoreButtonBox>
        )}
      </ThemeBox>
      <Section>
        <RegionSelect userRegions={userRegions} userGrade={userGrade} />
        <CategorySelect userGrade={userGrade} />
        <Divider />
        <OptionSectionBottom>
          <ResetButton
            onClick={() => {
              setSelectedRegions([]);
              setSelectedCategory([]);
            }}
          >
            <img src="/assets/images/icon/reset-black.svg" alt="" />
            초기화
          </ResetButton>
          <SelectedOptionContainer>
            <SelectedOptionSection>
              <SelectedOptionLabel>선택 지역</SelectedOptionLabel>
              <SelectedOptionWrapper>
                {selectedRegions.map((region) => (
                  <SelectedItem key={region}>{region}</SelectedItem>
                ))}
                {selectedRegions.length === 0 && (
                  <SelectedItem>전국</SelectedItem>
                )}
                {/* <SelectedItem>전국</SelectedItem> */}
              </SelectedOptionWrapper>
            </SelectedOptionSection>
            <SelectedOptionSection>
              <SelectedOptionLabel>선택 물건</SelectedOptionLabel>
              <SelectedOptionWrapper>
                {selectedCategory.map((category) => (
                  <SelectedItem>{category}</SelectedItem>
                ))}
                {selectedCategory.length === 0 && (
                  <SelectedItem>물건 전체</SelectedItem>
                )}
              </SelectedOptionWrapper>
            </SelectedOptionSection>
          </SelectedOptionContainer>
        </OptionSectionBottom>
      </Section>

      <Section>
        <ResultTable p1={p1} p2={p2} p3={p3} p4={p4} />
      </Section>

      {themeSelectModal && (
        <ThemeSelectModal
          selectedIndexes={indexes}
          isOpen={themeSelectModal}
          onClose={() => {
            setThemeSelectedModal(false);
          }}
        />
      )}
    </Box>
  );
};

export default Quant2;

const Box = styled.div<{ gap?: number }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap}px;
`;

const Row = styled.div<{ gap?: number }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.gap}px;
  align-items: center;
`;

const ThemeStock = styled.div`
  width: 22;
  height: 17;
  gap: 10px;
  padding-right: 5px;
  padding-left: 5px;
  border-radius: 4px;
  background: linear-gradient(127.44deg, #008aff 0%, #d224ca 100%);
  font-family: Spoqa Han Sans Neo;
  font-weight: 700;
  font-size: 12px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #ffffff;
`;

const Result_Row2 = styled(Row)<{ on?: boolean }>`
  width: 100%;
  border-radius: 12px;
  background: #ffffff;
  justify-content: space-between;
  align-items: center;
  border: ${(props) => (props.on ? '2px solid #008AFF' : '1px solid #e5e8ea')};
  padding: 24px 0;
`;

const Result_Row = styled(Row)`
  width: 100%;
  border-radius: 12px;
  background: #70737c0d;
  border: 1px solid #e5e8ea;
  justify-content: space-between;
  align-items: center;
`;

const Column_Line = styled.div`
  background: #e5e8ea;
  width: 1px;
  height: 51px;
`;

const Category_Box = styled.div<{ selected: boolean }>`
  padding: 7px;
  border-radius: 8px;
  background: #f7f8fc;
  border: ${(props) =>
    props.selected ? '2px solid #008AFF' : '2px solid #e5e8ea'};
  width: 46px;
  height: 46px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Category_Item = styled.button`
  gap: 8px;
  width: 54px;
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  border: none;
  background: none;
  user-select: none;
  padding: 0px;
`;

const Line_Selected = styled.div`
  width: 65px;
  height: 2px;
  background: #222222;
  top: 0;
  position: absolute;
  transition: 0.5s ease-in-out;
`;

const Line = styled.div`
  width: 1060px;
  height: 1px;
  background: #e1e2e4;
`;

const Region_Box = styled.button<{ disable?: boolean }>`
  border: none;
  background: none;
  cursor: ${({ disable }) => (disable ? 'default' : 'pointer')};
  padding: 14px 16px 12px;
  user-select: none;
  opacity: ${({ disable }) => (disable ? 0.4 : 1)};
`;

const TitleBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Text_Button = styled(Link)`
  text-decoration: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 10px 0;
  background: #fafafb;
  border: 1px solid #e5e8ea;
  width: 100%;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const PageTitle = styled.div`
  font-family: Spoqa Han Sans Neo;
  font-size: 24px;
  font-weight: 700;
  line-height: 34.8px;
  color: #171e26;
`;

const Description = styled(Box)`
  width: 100%;
  gap: 14px;
  border-radius: 16px;
  background: #eaf2fe;
  align-items: center;
  padding: 14px 16px;
  border: 1px solid #e5e8ea;
`;

const Text = styled.span<{ color: string; size: number; weight: number }>`
  font-family: Spoqa Han Sans Neo;
  font-weight: ${(props) => props.weight};
  font-size: ${(props) => props.size}px;
  line-height: 145%;
  letter-spacing: ${(props) => props.size * -0.02}px;
  text-align: center;
  color: ${(props) => props.color};
`;

const ItemBox = styled(Text)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ItemImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 16px;
`;
const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 7px;
`;

const ThemeBoxTitleSection = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const ThemeBoxTitle = styled.div`
  color: #222;
  font-size: 18px;
  font-weight: 700;
  line-height: 145%; /* 26.1px */
  letter-spacing: -0.36px;
`;

const ThemeBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;
  background-color: #ffffff;
`;

const SelectedThemeBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ThemeItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e5e8ea;
`;

const ThemeItemHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  font-weight: 700;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #222;
`;

const ThemeItemDescription = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;

  strong {
    font-weight: 700;
    color: #222;
  }
`;

const MoreButtonBox = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const MoreButton = styled.button<{ showAll?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  border-radius: 999px;
  gap: 2px;
  padding: 8px 16px;
  background-color: #f7f7f9;
  cursor: pointer;
  width: fit-content;
  border: none;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;

  img {
    transform: ${({ showAll }) =>
      showAll ? 'rotate(0deg)' : 'rotate(180deg)'};
  }
`;

const ThemeSelectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background-color: transparent;

  color: rgba(0, 12, 30, 0.8);
  font-size: 14px;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;

const Section = styled.div`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  background: #ffffff;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: rgba(112, 115, 124, 0.16);
`;

const OptionSectionBottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
`;

const ResetButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;

const SelectedOptionContainer = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 24px;
  border-radius: 16px;
  background: #f7f8fc;
`;

const SelectedOptionSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`;

const SelectedOptionLabel = styled.div`
  color: #616161;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%; /* 19.5px */
  letter-spacing: -0.26px;
`;

const SelectedOptionWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const SelectedItem = styled.div`
  display: flex;
  padding: 2px 12px;
  align-items: center;
  gap: 2px;
  border-radius: 9999px;
  background: #fff;

  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Table = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 16px;
`;

const TableLabelContainer = styled.div`
  display: flex;
  width: 96px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;

  background: #f7f8fc;
`;

const TableLabel = styled.div`
  display: flex;
  padding: 12px;
  align-items: center;
  align-self: stretch;
  width: 100%;

  color: #616161;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%; /* 19.5px */
  letter-spacing: -0.26px;
`;

const TableDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  flex: 1 0 0;
  border-bottom-right-radius: 16px;

  color: #222;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;

const TableCategory = styled.div<{ $hasSelect?: boolean }>`
  border-top-right-radius: 16px;
  display: flex;
  padding: 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  background: ${({ $hasSelect }) => ($hasSelect ? '#008AFF0F' : '#fff')};
`;

const TableData = styled.div<{ $hasSelect?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* border-radius: 16px; */
  border-bottom-right-radius: 16px;

  background: ${({ $hasSelect }) => ($hasSelect ? '#008AFF0F' : '#fff')};
`;

const TableDataItem = styled.div`
  display: flex;
  padding: 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const Addon = styled.div`
  display: flex;
  padding: 2px 12px;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #008aff;

  font-size: 12px;
  font-weight: 700;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
  color: #fff;
`;
